import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const RequiredDocumentsPage: React.FC = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  const [activeService, setActiveService] = useState('vehicle-registration');

  useSEO({
    title: 'Required Documents for DMV Services Las Vegas - Document Checklist Nevada',
    description: 'Complete document checklist for all Nevada DMV services in Las Vegas. Vehicle registration, title transfer, license plates, and notary documents required. Call 702.979.7202 to confirm.',
    keywords: 'DMV documents Las Vegas, vehicle registration documents Nevada, title transfer documents Las Vegas, required DMV paperwork Nevada',
    canonical: `${siteUrl}/required-documents`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Required Documents for DMV Services Las Vegas",
          "description": "Complete document checklist for all Nevada DMV services including vehicle registration, title transfer, and license plates.",
          "url": `${siteUrl}/required-documents`
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Required Documents', url: '/required-documents' }
        ])
      ]
    }
  });

  const documentRequirements = {
    'vehicle-registration': {
      title: 'Vehicle Registration',
      icon: 'ri-car-line',
      documents: [
        {
          category: 'Identity Documents',
          required: true,
          items: [
            'Valid Nevada Driver\'s License or ID',
            'Social Security Number or ITIN',
            'Proof of Nevada residency (utility bill, bank statement, etc.)'
          ]
        },
        {
          category: 'Vehicle Documents',
          required: true,
          items: [
            'Certificate of Title or Manufacturer\'s Statement of Origin',
            'Bill of Sale or Purchase Agreement',
            'Odometer Statement (if required)',
            'VIN Verification (Form VP-012)'
          ]
        },
        {
          category: 'Insurance & Inspection',
          required: true,
          items: [
            'Proof of Nevada Auto Insurance',
            'Safety Inspection (if applicable)',
            'Emissions Test (if required in your area)'
          ]
        },
        {
          category: 'Additional Documents',
          required: false,
          items: [
            'Lien Release (if paying off loan)',
            'Power of Attorney (if signing for someone else)',
            'Military ID (for military exemptions)'
          ]
        }
      ]
    },
    'title-transfer': {
      title: 'Title Transfer',
      icon: 'ri-file-transfer-line',
      documents: [
        {
          category: 'Seller Requirements',
          required: true,
          items: [
            'Original Certificate of Title (properly signed)',
            'Odometer Disclosure Statement',
            'Bill of Sale with selling price',
            'Valid ID of seller'
          ]
        },
        {
          category: 'Buyer Requirements',
          required: true,
          items: [
            'Valid Nevada Driver\'s License or ID',
            'Proof of Nevada residency',
            'Proof of Nevada Auto Insurance',
            'Social Security Number or ITIN'
          ]
        },
        {
          category: 'Vehicle Inspection',
          required: true,
          items: [
            'VIN Verification (Form VP-012)',
            'Safety Inspection (if applicable)',
            'Emissions Test (Clark County residents)'
          ]
        }
      ]
    },
    'license-plates': {
      title: 'License Plates',
      icon: 'ri-number-1',
      documents: [
        {
          category: 'Standard Plates',
          required: true,
          items: [
            'Valid Nevada Driver\'s License or ID',
            'Vehicle Registration Certificate',
            'Proof of Nevada Auto Insurance',
            'Current Registration renewal notice'
          ]
        },
        {
          category: 'Specialty Plates',
          required: true,
          items: [
            'All standard plate requirements',
            'Specialty plate application',
            'Proof of eligibility (if required)',
            'Additional documentation for organization plates'
          ]
        },
        {
          category: 'Personalized Plates',
          required: true,
          items: [
            'Personalized plate application',
            'Plate availability confirmation',
            'Standard registration documents',
            'Additional fees for personalization'
          ]
        }
      ]
    },
    'duplicate-title': {
      title: 'Duplicate Title',
      icon: 'ri-file-copy-line',
      documents: [
        {
          category: 'Owner Documents',
          required: true,
          items: [
            'Valid Nevada Driver\'s License or ID',
            'Proof of Nevada residency',
            'Social Security Number or ITIN',
            'Application for Duplicate Title (Form VP-012)'
          ]
        },
        {
          category: 'Vehicle Information',
          required: true,
          items: [
            'Vehicle Identification Number (VIN)',
            'License plate number',
            'Year, make, and model of vehicle',
            'Current registration information'
          ]
        },
        {
          category: 'Special Circumstances',
          required: false,
          items: [
            'Lien information (if applicable)',
            'Power of Attorney (if signing for owner)',
            'Court order (if title is disputed)',
            'Affidavit of lost title'
          ]
        }
      ]
    },
    'temporary-permits': {
      title: 'Temporary Permits',
      icon: 'ri-calendar-todo-line',
      documents: [
        {
          category: 'Movement Permits',
          required: true,
          items: [
            'Valid Driver\'s License or ID',
            'Proof of vehicle ownership',
            'Proof of Nevada Auto Insurance',
            'Reason for temporary movement'
          ]
        },
        {
          category: 'Registration Permits',
          required: true,
          items: [
            'All standard registration documents',
            'Temporary permit application',
            'Proof of pending paperwork',
            'Valid inspection certificates'
          ]
        }
      ]
    },
    'notary-services': {
      title: 'Notary Services',
      icon: 'ri-quill-pen-line',
      documents: [
        {
          category: 'Identification',
          required: true,
          items: [
            'Valid government-issued photo ID',
            'Driver\'s License or Passport',
            'State-issued ID card',
            'Military ID (active duty)'
          ]
        },
        {
          category: 'Documents to Notarize',
          required: true,
          items: [
            'Original documents (no copies)',
            'Completed forms ready for signature',
            'All pages of multi-page documents',
            'Properly filled out information'
          ]
        }
      ]
    }
  };

  const services = Object.keys(documentRequirements);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">Required Documents</h1>
              <p className="text-xl text-blue-100">
                Everything you need to bring for fast, efficient DMV services
              </p>
            </div>
          </div>
        </section>

        {/* Service Selector */}
        <section className="py-12 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Select Your Service</h2>
              <p className="text-gray-600">Choose the service you need to see the required documents</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {services.map((service) => {
                const serviceData = documentRequirements[service as keyof typeof documentRequirements];
                return (
                  <button
                    key={service}
                    onClick={() => setActiveService(service)}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      activeService === service
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300 text-gray-700'
                    }`}
                  >
                    <div className="text-center">
                      <i className={`${serviceData.icon} text-2xl mb-2 block`}></i>
                      <span className="text-sm font-medium">{serviceData.title}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Document Requirements */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-blue-600 text-white p-6">
                <div className="flex items-center">
                  <i className={`${documentRequirements[activeService as keyof typeof documentRequirements].icon} text-3xl mr-4`}></i>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {documentRequirements[activeService as keyof typeof documentRequirements].title}
                    </h2>
                    <p className="text-blue-100">Required documents and information</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  {documentRequirements[activeService as keyof typeof documentRequirements].documents.map((category, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">{category.category}</h3>
                        {category.required && (
                          <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                            Required
                          </span>
                        )}
                        {!category.required && (
                          <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                            Optional
                          </span>
                        )}
                      </div>
                      <ul className="space-y-2">
                        {category.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start">
                            <i className="ri-check-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Notes */}
        <section className="py-16 bg-yellow-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="ri-information-line text-yellow-600 text-3xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Important Notes</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-yellow-600 mr-2 mt-0.5"></i>
                      All documents must be original or certified copies - photocopies are not accepted
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-yellow-600 mr-2 mt-0.5"></i>
                      Foreign documents must be translated by a certified translator
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-yellow-600 mr-2 mt-0.5"></i>
                      Some services may require additional documents based on specific circumstances
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-yellow-600 mr-2 mt-0.5"></i>
                      Call ahead to confirm document requirements for your specific situation
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-yellow-600 mr-2 mt-0.5"></i>
                      Bring multiple forms of payment - cash, check, or money order may be required
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Required Documents?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Call us to verify what you need to bring for your specific situation
            </p>
            <a
              href="tel:7029797202"
              className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (702) 979-7202
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RequiredDocumentsPage;